/**
 * Public expo ticket-buy page (Feb 2026 R37).
 *
 * Two flows:
 *   /public/expo/:expoId/tickets      → buy tickets (tier list + form)
 *   /public/expo/tickets/:qrToken     → attendee ticket + RSVP page
 *
 * We keep both under one file so all the styling / layout is shared.
 * Cashfree drop-in is loaded lazily via a script tag (only when the
 * user actually clicks "Buy") so bots that browse the page don't burn
 * a Cashfree JS bundle download.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Ticket, MapPin, Calendar } from "lucide-react";

const BTN = "rounded-none";

export function PublicExpoTickets() {
  const { expoId } = useParams();
  const [data, setData] = useState(null);
  const [tier, setTier] = useState(null);
  const [form, setForm] = useState({ buyer_name: "", buyer_email: "", buyer_phone: "", quantity: 1 });
  const [busy, setBusy] = useState(false);
  const [purchased, setPurchased] = useState(null);

  useEffect(() => {
    api.get(`/public/organizer/expos/${expoId}/tiers`)
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }));
  }, [expoId]);

  const buy = async () => {
    if (!tier) return toast.error("Pick a tier");
    if (!form.buyer_name || !form.buyer_email) return toast.error("Name & email required");
    setBusy(true);
    try {
      const { data: r } = await api.post(`/public/organizer/expos/${expoId}/checkout`, {
        tier_id: tier.id, buyer_name: form.buyer_name, buyer_email: form.buyer_email,
        buyer_phone: form.buyer_phone || null, quantity: +form.quantity || 1,
        return_url: `${window.location.origin}/public/expo/tickets/${(r?.qr_tokens || [""])[0]}`,
      });
      setPurchased(r);
      // Load Cashfree drop-in and open the payment session
      if (r.payment_session_id) {
        const isTest = r.mode === "TEST";
        const jsUrl = isTest
          ? "https://sdk.cashfree.com/js/v3/cashfree.js"
          : "https://sdk.cashfree.com/js/v3/cashfree.js";
        if (!window.Cashfree) {
          await new Promise((resolve, reject) => {
            const s = document.createElement("script");
            s.src = jsUrl; s.onload = resolve; s.onerror = reject; document.body.appendChild(s);
          });
        }
        // eslint-disable-next-line no-undef
        const cf = window.Cashfree({ mode: isTest ? "sandbox" : "production" });
        await cf.checkout({ paymentSessionId: r.payment_session_id, redirectTarget: "_self" });
      } else {
        toast.warning("Payments not configured — ticket saved as pending");
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Checkout failed")); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;
  if (data.error) return <div className="p-12 text-center text-muted-foreground">Sorry — this expo isn&apos;t taking bookings right now.</div>;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="public-expo-tickets">
      <div className="border border-border bg-muted/20 p-6">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Expo</div>
        <h1 className="text-3xl font-black">{data.expo.name}</h1>
        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {data.expo.start_date} → {data.expo.end_date}</span>
          {data.expo.venue_name && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {data.expo.venue_name}, {data.expo.city}</span>}
        </div>
      </div>

      {purchased && purchased.qr_tokens?.length > 0 && (
        <div className="border border-emerald-400 bg-emerald-50 p-4 text-sm" data-testid="purchase-success">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 inline-block mr-2" />
          Order created. Redirecting to payment… Your ticket QR:
          {purchased.qr_tokens.map((t, i) => (
            <div key={i} className="mt-2">
              <a href={`/public/expo/tickets/${t}`} className="text-primary underline font-mono">{t}</a>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <div className="label-eyebrow">Pick a ticket</div>
        {data.tiers.length === 0 && <div className="text-sm text-muted-foreground italic">No tiers on sale yet.</div>}
        {data.tiers.map((t) => (
          <button
            key={t.id}
            onClick={() => setTier(t)}
            className={`w-full text-left border p-4 transition-colors ${
              tier?.id === t.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
            } ${t.remaining === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
            disabled={t.remaining === 0}
            data-testid={`public-tier-${t.id}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold">{t.name}</div>
                {t.description && <div className="text-xs text-muted-foreground">{t.description}</div>}
              </div>
              <div className="text-right">
                <div className="text-lg font-mono font-bold">{t.currency} {Number(t.price_amount).toLocaleString()}</div>
                {t.remaining !== null && <div className="text-[10px] text-muted-foreground">{t.remaining} left</div>}
              </div>
            </div>
          </button>
        ))}
      </div>

      {tier && (
        <div className="border border-border p-4 space-y-3" data-testid="buyer-form">
          <div className="label-eyebrow">Buyer details</div>
          <div><Label>Name *</Label>
            <Input className={`${BTN} h-10`} value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} data-testid="buyer-name" /></div>
          <div><Label>Email *</Label>
            <Input type="email" className={`${BTN} h-10`} value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })} data-testid="buyer-email" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Phone</Label>
              <Input className={`${BTN} h-10`} value={form.buyer_phone} onChange={(e) => setForm({ ...form, buyer_phone: e.target.value })} /></div>
            <div><Label>Quantity</Label>
              <Input type="number" min="1" max="10" className={`${BTN} h-10`} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
          </div>
          <Button className={`${BTN} w-full`} onClick={buy} disabled={busy} data-testid="buyer-submit">
            <Ticket className="w-4 h-4 mr-1" />
            {busy ? "Creating order…" : `Pay ${tier.currency} ${(tier.price_amount * (+form.quantity || 1)).toLocaleString()}`}
          </Button>
        </div>
      )}
    </div>
  );
}

export function PublicTicketDetail() {
  const { qrToken } = useParams();
  const [ticket, setTicket] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [rsvps, setRsvps] = useState([]);

  useEffect(() => {
    api.get(`/public/organizer/tickets/${qrToken}`).then((r) => setTicket(r.data)).catch(() => setTicket({ error: true }));
    api.get(`/public/organizer/tickets/${qrToken}/rsvps`).then((r) => setRsvps(r.data.rsvps || [])).catch(() => {});
  }, [qrToken]);

  useEffect(() => {
    if (!ticket?.ticket?.expo_id) return;
    api.get(`/public/organizer/expos/${ticket.ticket.expo_id}/sessions`)
      .then((r) => setSessions(r.data.sessions || [])).catch(() => setSessions([]));
  }, [ticket]);

  const rsvpFor = async (session) => {
    try {
      await api.post(`/public/organizer/rsvps`, { qr_token: qrToken, session_id: session.id });
      toast.success("RSVP confirmed");
      const r = await api.get(`/public/organizer/tickets/${qrToken}/rsvps`);
      setRsvps(r.data.rsvps || []);
    } catch (e) { toast.error(apiErrorMessage(e, "RSVP failed")); }
  };

  if (!ticket) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;
  if (ticket.error) return <div className="p-12 text-center text-muted-foreground">Ticket not found.</div>;

  const t = ticket.ticket;
  const qrDataUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrToken)}`;
  const rsvpedIds = new Set(rsvps.map((r) => r.session_id));

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="public-ticket-detail">
      <div className="border border-border p-6 flex items-center gap-6">
        <img src={qrDataUrl} alt="QR code" className="w-32 h-32 border border-border" data-testid="ticket-qr" />
        <div>
          <div className="label-eyebrow">Ticket</div>
          <h1 className="text-2xl font-bold">{t.buyer_name}</h1>
          <div className="text-sm text-muted-foreground">{t.tier_name} · {t.currency} {t.amount}</div>
          <div className="mt-1"><span className={`chip text-[11px] ${t.status === "paid" ? "bg-emerald-100 text-emerald-800 border-emerald-400" : t.status === "checked_in" ? "bg-sky-100 text-sky-800 border-sky-400" : t.status === "pending" ? "bg-amber-100 text-amber-800 border-amber-400" : "bg-rose-100 text-rose-800 border-rose-400"}`}>{t.status}</span></div>
        </div>
      </div>
      {ticket.expo && (
        <div className="text-sm text-muted-foreground">
          <b>{ticket.expo.name}</b> · {ticket.expo.start_date} → {ticket.expo.end_date} · {ticket.expo.venue_name}
        </div>
      )}
      {t.status !== "paid" && t.status !== "checked_in" && (
        <div className="border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900">
          Payment is <b>{t.status}</b>. RSVPs open once payment completes.
        </div>
      )}
      {(t.status === "paid" || t.status === "checked_in") && sessions && (
        <div className="space-y-2" data-testid="ticket-sessions">
          <div className="label-eyebrow">Reserve sessions</div>
          {sessions.length === 0 && <div className="text-sm text-muted-foreground italic">No sessions listed yet.</div>}
          {sessions.map((s) => {
            const rsvped = rsvpedIds.has(s.id);
            const full = s.capacity && s.remaining === 0;
            return (
              <div key={s.id} className="border border-border p-3 flex items-center justify-between" data-testid={`public-session-${s.id}`}>
                <div>
                  <div className="text-[10px] text-muted-foreground">Track · {s.track_name}{s.room_name ? ` · ${s.room_name}` : ""}</div>
                  <div className="font-semibold text-sm">{s.title}</div>
                  <div className="text-xs text-muted-foreground">{(s.start_at || "").slice(0, 16).replace("T", " ")} → {(s.end_at || "").slice(11, 16)}{s.speaker_name ? ` · 🎤 ${s.speaker_name}` : ""}</div>
                </div>
                <Button size="sm" className={BTN} onClick={() => rsvpFor(s)} disabled={rsvped || full} data-testid={`rsvp-btn-${s.id}`}>
                  {rsvped ? "Reserved" : full ? "Full" : "RSVP"}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
